import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  HealthProvider,
  IHealthProvider,
  HealthActivity,
  PermissionStatus,
  ActivityType,
  TRAINING_TYPE_TO_ACTIVITY,
  EXERCISE_TYPE_TO_ACTIVITY,
  HealthSyncMetadata
} from './types';
import { WebHealthProvider } from './providers/web-provider';
import { AppleHealthProvider } from './providers/apple-health-provider';
import { updateData, getData, setData } from '../firebase/api-wrapper.service';
import { getCurrentUser } from '../firebase/auth.service';
import { getTrainings } from '../firebase/training.service';
import { getExercises, addExercise } from '../firebase/exercise.service';
import { getUserWorkoutSessions } from '../firebase/workout-session.service';
import logger from '../../utils/logger';
import {
  HealthProvidersMetadata,
  HEALTH_PROVIDER_IDS,
  HealthKitWorkout,
  ImportedWorkoutIndex
} from '../../types/health';
import { ExerciseType } from '../../types/exercise';
import { STORAGE_KEY } from './constants';

export interface ManualSyncResult {
  trainings: { synced: number; failed: number; skipped: number };
  exercises: { synced: number; failed: number; skipped: number };
  workouts: { synced: number; failed: number; skipped: number };
  totalSynced: number;
  totalFailed: number;
}

export interface HealthKitQueryResult {
  workouts: HealthKitWorkout[];
  totalCount: number;
}

export interface ImportResultDetails {
  /** Number of workouts removed by time-overlap deduplication algorithm */
  deduplicated: number;
  /** Number of workouts already in the import index (previously imported) */
  alreadyImported: number;
  /** First 5 error messages from failed imports */
  errors: string[];
}

export interface ImportResult {
  imported: number;
  skipped: number;
  failed: number;
  importedIds: string[];
  details: ImportResultDetails;
}

export interface ResyncResult {
  export: ManualSyncResult;
  import: ImportResult;
}

export type ResyncPhase = 'export' | 'import';

class HealthSyncService {
  private provider: IHealthProvider;
  private syncMetadata: HealthSyncMetadata | null = null;

  // Background sync throttling
  private lastBackgroundSyncTime: number = 0;
  private static readonly BACKGROUND_SYNC_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes

  // HealthKit query cache (5-minute TTL)
  private queryCache: { workouts: HealthKitWorkout[]; timestamp: number; daysBack: number } | null = null;
  private static readonly QUERY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // Initialize with appropriate provider based on platform
    this.provider = this.createProvider();
  }
  
  /**
   * Create health provider based on platform
   */
  private createProvider(): IHealthProvider {
    const platform = Capacitor.getPlatform();

    switch (platform) {
      case 'ios':
        logger.info('iOS platform detected - using Apple Health provider');
        return new AppleHealthProvider();

      case 'android':
        // TODO: Implement GoogleFitProvider when Health Connect plugin is available
        logger.info('Android platform detected - Google Fit/Health Connect support pending');
        return new WebHealthProvider();

      default:
        return new WebHealthProvider();
    }
  }
  
  /**
   * Check if health sync is available
   */
  async isAvailable(): Promise<boolean> {
    return this.provider.isAvailable();
  }
  
  /**
   * Get current provider name
   */
  getProviderName(): HealthProvider {
    return this.provider.name;
  }
  
  /**
   * Request health permissions
   */
  async requestPermissions(): Promise<PermissionStatus> {
    try {
      const status = await this.provider.requestPermissions();
      logger.info('Health permissions requested', { status });
      return status;
    } catch (error) {
      logger.error('Error requesting health permissions', error);
      return PermissionStatus.DENIED;
    }
  }
  
  /**
   * Check health permissions
   */
  async checkPermissions(): Promise<PermissionStatus> {
    return this.provider.checkPermissions();
  }

  /**
   * Perform background health sync on app open/resume
   * This is a non-blocking operation that syncs unsynced items and imports new workouts
   * Includes 15-minute throttling to prevent excessive syncing
   */
  async performBackgroundSync(): Promise<void> {
    try {
      // 0. Throttle check - skip if synced recently
      const now = Date.now();
      if (now - this.lastBackgroundSyncTime < HealthSyncService.BACKGROUND_SYNC_COOLDOWN_MS) {
        logger.info('🏥 Skipping background sync - within 15 minute cooldown');
        return;
      }

      // 1. Platform check
      if (!(await this.isAvailable())) {
        return; // Not iOS or plugin unavailable
      }

      // 2. User enabled check (local preferences)
      if (!(await this.isProviderEnabled())) {
        return; // User hasn't enabled health sync
      }

      // 3. Permission check
      const permissions = await this.checkPermissions();
      if (permissions !== PermissionStatus.AUTHORIZED) {
        return; // Permissions not granted
      }

      // Update last sync time BEFORE starting (to prevent concurrent syncs)
      this.lastBackgroundSyncTime = now;

      // 4. Export: Sync unsynced Marune items to Apple Health (7 day lookback)
      logger.info('🏥 Starting background health sync (export)...');
      const exportResult = await this.syncAllUnsynced(7);
      logger.info('🏥 Background export complete', {
        synced: exportResult.totalSynced,
        failed: exportResult.totalFailed
      });

      // 5. Import: Query Apple Health workouts and import as exercises (7 days)
      logger.info('🏥 Starting background health import...');
      const queryResult = await this.queryHealthWorkouts(7);
      if (queryResult.workouts.length > 0) {
        const importResult = await this.importHealthWorkouts(queryResult.workouts);
        logger.info('🏥 Background import complete', {
          imported: importResult.imported,
          skipped: importResult.skipped,
          failed: importResult.failed
        });
      } else {
        logger.info('🏥 No new workouts to import from Apple Health');
      }
    } catch (error) {
      // Silent failure - don't break app startup
      logger.error('Background health sync error:', error);
    }
  }

  /**
   * Load sync metadata from Firebase
   */
  private async loadSyncMetadata(): Promise<void> {
    const user = getCurrentUser();
    if (!user) return;
    
    try {
      const metadata = await getData(`users/${user.uid}/healthSyncMetadata`);
      this.syncMetadata = metadata || {
        provider: this.provider.name,
        syncedItems: {}
      };
    } catch (error) {
      logger.error('Error loading health sync metadata', error);
      this.syncMetadata = {
        provider: this.provider.name,
        syncedItems: {}
      };
    }
  }
  
  /**
   * Save sync metadata to Firebase
   */
  private async saveSyncMetadata(): Promise<void> {
    const user = getCurrentUser();
    if (!user || !this.syncMetadata) return;
    
    try {
      await updateData(`users/${user.uid}/healthSyncMetadata`, {
        ...this.syncMetadata,
        lastSyncDate: Date.now()
      });
    } catch (error) {
      logger.error('Error saving health sync metadata', error);
    }
  }
  
  /**
   * Check if item has been synced
   */
  private async isItemSynced(itemId: string): Promise<boolean> {
    if (!this.syncMetadata) {
      await this.loadSyncMetadata();
    }
    
    return !!this.syncMetadata?.syncedItems[itemId]?.success;
  }
  
  /**
   * Mark item as synced
   */
  private async markItemSynced(itemId: string, success: boolean, error?: string): Promise<void> {
    if (!this.syncMetadata) {
      await this.loadSyncMetadata();
    }
    
    if (this.syncMetadata) {
      this.syncMetadata.syncedItems[itemId] = {
        syncDate: Date.now(),
        success,
        error
      };
      
      await this.saveSyncMetadata();
    }
  }
  
  /**
   * Sync training session to health provider
   */
  async syncTraining(training: any): Promise<boolean> {
    try {
      // Check if already synced
      if (await this.isItemSynced(`training_${training.id}`)) {
        logger.info('Training already synced', { id: training.id });
        return true;
      }
      
      // Check permissions
      const permissions = await this.checkPermissions();
      if (permissions !== PermissionStatus.AUTHORIZED) {
        logger.warn('Health sync not authorized');
        return false;
      }
      
      // Map training type to activity type
      const activityType = TRAINING_TYPE_TO_ACTIVITY[training.trainedStyle?.toLowerCase()] || ActivityType.MARTIAL_ARTS;
      
      // Create health activity
      const activity: HealthActivity = {
        type: activityType,
        startDate: new Date(training.date).toISOString(),
        endDate: new Date(training.date + (training.totalTrainedTime * 60 * 1000)).toISOString(),
        duration: training.totalTrainedTime * 60, // Convert minutes to seconds
        notes: training.note,
        source: 'marune'
      };
      
      // Save to health provider
      const success = await this.provider.saveActivity(activity);
      
      // Mark as synced
      await this.markItemSynced(`training_${training.id}`, success);
      
      logger.info('Training synced to health', { id: training.id, success });
      return success;
    } catch (error: any) {
      logger.error('Error syncing training', error);
      await this.markItemSynced(`training_${training.id}`, false, error?.message || 'Unknown error');
      return false;
    }
  }
  
  /**
   * Sync exercise to health provider
   */
  async syncExercise(exercise: any): Promise<boolean> {
    try {
      // Check if already synced
      if (await this.isItemSynced(`exercise_${exercise.id}`)) {
        logger.info('Exercise already synced', { id: exercise.id });
        return true;
      }
      
      // Check permissions
      const permissions = await this.checkPermissions();
      if (permissions !== PermissionStatus.AUTHORIZED) {
        logger.warn('Health sync not authorized');
        return false;
      }
      
      // Map exercise type to activity type
      const activityType = EXERCISE_TYPE_TO_ACTIVITY[exercise.type?.toLowerCase()] || ActivityType.OTHER;
      
      // Create health activity
      const activity: HealthActivity = {
        type: activityType,
        startDate: new Date(exercise.date).toISOString(),
        endDate: new Date(exercise.date + (exercise.duration * 60 * 1000)).toISOString(),
        duration: exercise.duration * 60, // Convert minutes to seconds
        calories: exercise.caloriesBurned,
        notes: exercise.notes,
        source: 'marune'
      };
      
      // Save to health provider
      const success = await this.provider.saveActivity(activity);
      
      // Mark as synced
      await this.markItemSynced(`exercise_${exercise.id}`, success);
      
      logger.info('Exercise synced to health', { id: exercise.id, success });
      return success;
    } catch (error: any) {
      logger.error('Error syncing exercise', error);
      await this.markItemSynced(`exercise_${exercise.id}`, false, error?.message || 'Unknown error');
      return false;
    }
  }
  
  /**
   * Sync workout session to health provider
   */
  async syncWorkout(workout: any): Promise<boolean> {
    try {
      // Check if already synced
      if (await this.isItemSynced(`workout_${workout.id}`)) {
        logger.info('Workout already synced', { id: workout.id });
        return true;
      }
      
      // Check permissions
      const permissions = await this.checkPermissions();
      if (permissions !== PermissionStatus.AUTHORIZED) {
        logger.warn('Health sync not authorized');
        return false;
      }
      
      // Create health activity
      const activity: HealthActivity = {
        type: ActivityType.CROSS_TRAINING,
        startDate: new Date(workout.startTime).toISOString(),
        endDate: new Date(workout.endTime).toISOString(),
        duration: Math.floor((workout.endTime - workout.startTime) / 1000), // Convert ms to seconds
        notes: workout.name,
        source: 'marune'
      };
      
      // Save to health provider
      const success = await this.provider.saveActivity(activity);
      
      // Mark as synced
      await this.markItemSynced(`workout_${workout.id}`, success);
      
      logger.info('Workout synced to health', { id: workout.id, success });
      return success;
    } catch (error: any) {
      logger.error('Error syncing workout', error);
      await this.markItemSynced(`workout_${workout.id}`, false, error?.message || 'Unknown error');
      return false;
    }
  }
  
  /**
   * Get activities from health provider
   */
  async getActivities(startDate: Date, endDate: Date): Promise<HealthActivity[]> {
    try {
      const permissions = await this.checkPermissions();
      if (permissions !== PermissionStatus.AUTHORIZED) {
        logger.warn('Health sync not authorized');
        return [];
      }

      return this.provider.getActivities(startDate, endDate);
    } catch (error) {
      logger.error('Error getting activities from health', error);
      return [];
    }
  }

  // ============================================================================
  // NEW METHODS: Original Marune app compatible metadata helpers
  // ============================================================================

  /**
   * Get the current provider ID for Firebase metadata
   * Returns the provider ID that matches original Marune app format
   */
  getCurrentProviderId(): string {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return HEALTH_PROVIDER_IDS.IOS_HEALTHKIT;
    } else if (platform === 'android') {
      return HEALTH_PROVIDER_IDS.ANDROID_GOOGLE_FIT;
    }
    return '';
  }

  /**
   * Check if an entity has already been synced to the current health provider
   * Uses the healthProvidersMetadata field from original Marune app
   *
   * @param entity - Training, Exercise, or WorkoutSession with optional healthProvidersMetadata
   */
  isSyncedToCurrentProvider(entity: { healthProvidersMetadata?: HealthProvidersMetadata }): boolean {
    const providerId = this.getCurrentProviderId();
    if (!providerId) return false;

    return entity.healthProvidersMetadata?.sync?.[providerId as keyof HealthProvidersMetadata['sync']] === true;
  }

  /**
   * Create metadata update object for marking an entity as synced
   * Returns object that can be spread into Firebase update call
   *
   * @param entity - Entity with existing healthProvidersMetadata (optional)
   * @param syncResult - Record of provider IDs and their sync success status
   */
  makeHealthProvidersMetadata(
    entity: { healthProvidersMetadata?: HealthProvidersMetadata },
    syncResult: Record<string, boolean>
  ): { healthProvidersMetadata: HealthProvidersMetadata } {
    return {
      healthProvidersMetadata: {
        sync: {
          ...entity.healthProvidersMetadata?.sync,
          ...syncResult,
        },
      },
    };
  }

  /**
   * Check if a specific health provider is enabled in user settings
   * Settings are stored locally in Preferences (not Firebase)
   */
  async isProviderEnabled(providerId?: string): Promise<boolean> {
    const id = providerId || this.getCurrentProviderId();
    if (!id) return false;

    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      if (!value) return false;

      const enabledProviders = JSON.parse(value) as Record<string, boolean>;
      return enabledProviders[id] === true;
    } catch (error) {
      logger.error('Error checking provider enabled status', error);
      return false;
    }
  }

  /**
   * Enable or disable a health provider
   * On enable, will request permissions if not already granted
   *
   * @param enabled - Whether to enable or disable the provider
   * @param providerId - Optional provider ID (defaults to current platform)
   */
  async setProviderEnabled(enabled: boolean, providerId?: string): Promise<void> {
    const id = providerId || this.getCurrentProviderId();
    if (!id) {
      throw new Error('No health provider available for this platform');
    }

    // If enabling, request permissions first
    if (enabled) {
      const status = await this.requestPermissions();
      if (status !== PermissionStatus.AUTHORIZED) {
        throw new Error('Health permissions not granted');
      }
    }

    try {
      // Load existing settings
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      const enabledProviders = value ? JSON.parse(value) : {};

      // Update settings
      enabledProviders[id] = enabled;

      // Save settings
      await Preferences.set({
        key: STORAGE_KEY,
        value: JSON.stringify(enabledProviders),
      });

      logger.info(`Health provider ${id} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      logger.error('Error setting provider enabled status', error);
      throw error;
    }
  }

  /**
   * Get all enabled providers
   */
  async getEnabledProviders(): Promise<Record<string, boolean>> {
    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      if (!value) return {};
      return JSON.parse(value);
    } catch (error) {
      logger.error('Error getting enabled providers', error);
      return {};
    }
  }

  /**
   * Manually sync all unsynced items to health provider
   * Syncs trainings, exercises, and workout sessions from the last 30 days
   *
   * @param daysBack - Number of days to look back (default 30)
   * @param onProgress - Optional callback for progress updates
   */
  async syncAllUnsynced(
    daysBack: number = 30,
    onProgress?: (current: number, total: number, item: string) => void
  ): Promise<ManualSyncResult> {
    const result: ManualSyncResult = {
      trainings: { synced: 0, failed: 0, skipped: 0 },
      exercises: { synced: 0, failed: 0, skipped: 0 },
      workouts: { synced: 0, failed: 0, skipped: 0 },
      totalSynced: 0,
      totalFailed: 0
    };

    const user = getCurrentUser();
    if (!user) {
      logger.error('No user logged in for manual sync');
      return result;
    }

    // Check permissions
    const permissions = await this.checkPermissions();
    if (permissions !== PermissionStatus.AUTHORIZED) {
      logger.warn('Health sync not authorized for manual sync');
      return result;
    }

    const providerId = this.getCurrentProviderId();
    // If daysBack is 0, sync all time (use 0 as cutoff date)
    const cutoffDate = daysBack > 0
      ? Date.now() - (daysBack * 24 * 60 * 60 * 1000)
      : 0;

    logger.info(`Starting manual health sync${daysBack > 0 ? ` for last ${daysBack} days` : ' for all time'}`, {
      cutoffDate,
      cutoffDateReadable: cutoffDate > 0 ? new Date(cutoffDate).toISOString() : 'All Time'
    });

    try {
      // Fetch all items
      logger.info('Fetching exercises from path:', `exercises/${user.uid}`);
      const [trainings, exercises, workouts] = await Promise.all([
        getTrainings(user.uid).catch((e) => { logger.error('Error fetching trainings', e); return []; }),
        getExercises(user.uid).catch((e) => {
          logger.error('Error fetching exercises', { error: e, message: e?.message, code: e?.code });
          return [];
        }),
        getUserWorkoutSessions(user.uid).catch((e) => { logger.error('Error fetching workout sessions', e); return []; })
      ]);

      logger.info('Raw exercises returned:', {
        count: exercises.length,
        types: exercises.map(e => e.type),
        keys: exercises.map(e => e.exerciseKey)
      });

      logger.info(`Fetched raw data`, {
        trainingsCount: trainings.length,
        exercisesCount: exercises.length,
        workoutsCount: workouts.length
      });

      // Log exercise dates for debugging
      if (exercises.length > 0) {
        exercises.forEach((e, i) => {
          logger.info(`Exercise ${i} date debug`, {
            date: e.date,
            dateCreated: e.dateCreated,
            dateAsISO: e.date ? new Date(e.date).toISOString() : 'undefined',
            dateCreatedAsISO: e.dateCreated ? new Date(e.dateCreated).toISOString() : 'undefined',
            cutoffDate,
            isRecentByDate: e.date >= cutoffDate,
            isRecentByDateCreated: e.dateCreated >= cutoffDate
          });
        });
      }

      // Filter to recent items only
      // Note: Dates in Firebase are stored in milliseconds
      // Use date OR dateCreated for exercises (whichever exists)
      const recentTrainings = trainings.filter(t => t.date >= cutoffDate);
      const recentExercises = exercises.filter(e => (e.date || e.dateCreated) >= cutoffDate);
      const recentWorkouts = workouts.filter(w => w.dateCreated >= cutoffDate);

      logger.info(`Date filtering results`, {
        totalTrainings: trainings.length,
        recentTrainings: recentTrainings.length,
        totalExercises: exercises.length,
        recentExercises: recentExercises.length
      });

      const totalItems = recentTrainings.length + recentExercises.length + recentWorkouts.length;
      let currentItem = 0;

      logger.info(`Found ${totalItems} items to potentially sync`, {
        trainings: recentTrainings.length,
        exercises: recentExercises.length,
        workouts: recentWorkouts.length
      });

      // Sync trainings
      for (const training of recentTrainings) {
        currentItem++;
        onProgress?.(currentItem, totalItems, `Training: ${training.trainedStyle || 'Session'}`);

        // Check if already synced using healthProvidersMetadata
        const trainingSyncStatus = training.healthProvidersMetadata?.sync as Record<string, boolean> | undefined;
        if (trainingSyncStatus?.[providerId]) {
          result.trainings.skipped++;
          continue;
        }

        try {
          const success = await this.syncTraining(training);
          if (success) {
            result.trainings.synced++;
            result.totalSynced++;

            // Update training with health sync metadata
            const healthMetadata = this.makeHealthProvidersMetadata(training, { [providerId]: true });
            await updateData(`trainings/${training.id}`, healthMetadata);
          } else {
            result.trainings.failed++;
            result.totalFailed++;
          }
        } catch (error) {
          result.trainings.failed++;
          result.totalFailed++;
          logger.error('Error syncing training in manual sync', { id: training.id, error });
        }
      }

      // Sync exercises
      for (const exercise of recentExercises) {
        currentItem++;
        onProgress?.(currentItem, totalItems, `Exercise: ${exercise.type || 'Activity'}`);

        // Check if already synced
        const exerciseSyncStatus = exercise.healthProvidersMetadata?.sync as Record<string, boolean> | undefined;
        if (exerciseSyncStatus?.[providerId]) {
          result.exercises.skipped++;
          continue;
        }

        try {
          const success = await this.syncExercise(exercise);
          if (success) {
            result.exercises.synced++;
            result.totalSynced++;

            // Update exercise with health sync metadata
            const healthMetadata = this.makeHealthProvidersMetadata(exercise, { [providerId]: true });
            await updateData(`exercises/${user.uid}/${exercise.exerciseKey}`, healthMetadata);
          } else {
            result.exercises.failed++;
            result.totalFailed++;
          }
        } catch (error) {
          result.exercises.failed++;
          result.totalFailed++;
          logger.error('Error syncing exercise in manual sync', { key: exercise.exerciseKey, error });
        }
      }

      // Sync workouts
      for (const workout of recentWorkouts) {
        currentItem++;
        onProgress?.(currentItem, totalItems, `Workout: Session`);

        // Check if already synced
        const workoutSyncStatus = workout.healthProvidersMetadata?.sync as Record<string, boolean> | undefined;
        if (workoutSyncStatus?.[providerId]) {
          result.workouts.skipped++;
          continue;
        }

        try {
          const success = await this.syncWorkout(workout);
          if (success) {
            result.workouts.synced++;
            result.totalSynced++;

            // Update workout with health sync metadata
            const healthMetadata = this.makeHealthProvidersMetadata(workout, { [providerId]: true });
            await updateData(`workoutSessions/${user.uid}/${workout.sessionKey}`, healthMetadata);
          } else {
            result.workouts.failed++;
            result.totalFailed++;
          }
        } catch (error) {
          result.workouts.failed++;
          result.totalFailed++;
          logger.error('Error syncing workout in manual sync', { key: workout.sessionKey, error });
        }
      }

      logger.info('Manual health sync completed', result);
      return result;
    } catch (error) {
      logger.error('Error in manual health sync', error);
      return result;
    }
  }

  /**
   * Query workouts from Apple Health / Google Fit
   * Used to import external workouts into Marune
   *
   * @param daysBack - Number of days to query (0 = all time, but limited to 365 days max for performance)
   * @returns Array of workouts from the health provider
   */
  async queryHealthWorkouts(daysBack: number = 30): Promise<HealthKitQueryResult> {
    const result: HealthKitQueryResult = {
      workouts: [],
      totalCount: 0
    };

    // Return cached result if still valid and same date range
    if (
      this.queryCache &&
      this.queryCache.daysBack === daysBack &&
      Date.now() - this.queryCache.timestamp < HealthSyncService.QUERY_CACHE_TTL_MS
    ) {
      logger.info('Returning cached health workouts query', {
        count: this.queryCache.workouts.length,
        cacheAge: Math.round((Date.now() - this.queryCache.timestamp) / 1000) + 's'
      });
      return {
        workouts: this.queryCache.workouts,
        totalCount: this.queryCache.workouts.length
      };
    }

    // Check permissions
    const permissions = await this.checkPermissions();
    if (permissions !== PermissionStatus.AUTHORIZED) {
      logger.warn('Health sync not authorized for querying workouts');
      return result;
    }

    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();

    if (daysBack > 0) {
      startDate.setDate(startDate.getDate() - daysBack);
    } else {
      // All time - but limit to 365 days for performance
      startDate.setDate(startDate.getDate() - 365);
    }

    logger.info('Querying health workouts', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      daysBack
    });

    try {
      // Only AppleHealthProvider has queryWorkouts
      if (this.provider instanceof AppleHealthProvider) {
        const workouts = await this.provider.queryWorkouts(startDate, endDate, {
          excludeMaruneWorkouts: true, // Don't import workouts we created
          limit: 500
        });

        result.workouts = workouts;
        result.totalCount = workouts.length;

        // Cache the result
        this.queryCache = {
          workouts,
          timestamp: Date.now(),
          daysBack
        };

        logger.info('Found workouts from health provider', {
          count: workouts.length,
          sources: [...new Set(workouts.map(w => w.source))]
        });
      } else {
        logger.warn('Current health provider does not support querying workouts');
      }

      return result;
    } catch (error) {
      logger.error('Error querying health workouts', error);
      return result;
    }
  }

  /**
   * Detect and remove duplicate workouts from different sources
   * Uses time overlap detection - if two workouts overlap by 50%+ they're considered duplicates
   * Keeps the workout with more data (higher calorie count or longer duration as proxy for data quality)
   *
   * @param workouts - Array of HealthKit workouts
   * @returns Deduplicated array of workouts
   */
  private deduplicateWorkouts(workouts: HealthKitWorkout[]): HealthKitWorkout[] {
    if (workouts.length <= 1) return workouts;

    // Sort by start date
    const sorted = [...workouts].sort((a, b) =>
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    const result: HealthKitWorkout[] = [];
    const skippedIndices = new Set<number>();

    for (let i = 0; i < sorted.length; i++) {
      if (skippedIndices.has(i)) continue;

      const current = sorted[i];
      const currentStart = new Date(current.startDate).getTime();
      const currentEnd = new Date(current.endDate).getTime();
      const currentDuration = currentEnd - currentStart;

      // Look for overlapping workouts
      let bestWorkout = current;

      for (let j = i + 1; j < sorted.length; j++) {
        if (skippedIndices.has(j)) continue;

        const other = sorted[j];
        const otherStart = new Date(other.startDate).getTime();
        const otherEnd = new Date(other.endDate).getTime();

        // Duplicates from multiple devices always cover the same time period.
        // If the next workout starts more than 24 hours later, no more overlaps possible.
        if (otherStart - currentStart > 24 * 60 * 60 * 1000) break;

        // If other starts after current ends, no more overlaps possible (sorted by start)
        if (otherStart >= currentEnd) break;

        // Calculate overlap
        const overlapStart = Math.max(currentStart, otherStart);
        const overlapEnd = Math.min(currentEnd, otherEnd);
        const overlapDuration = Math.max(0, overlapEnd - overlapStart);

        // Check if overlap is significant (50% of either workout)
        const overlapPercentCurrent = overlapDuration / currentDuration;
        const overlapPercentOther = overlapDuration / (otherEnd - otherStart);

        if (overlapPercentCurrent >= 0.5 || overlapPercentOther >= 0.5) {
          // These are duplicates - keep the one with better data
          // Prefer: higher calories > longer duration > more recent source
          // Compare against the running bestWorkout (not the fixed original)
          // so the richest workout survives across a group of 3+ overlaps
          const bestScore = (bestWorkout.totalEnergyBurned || 0) + (bestWorkout.duration / 60);
          const otherScore = (other.totalEnergyBurned || 0) + (other.duration / 60);

          if (otherScore > bestScore) {
            bestWorkout = other;
          }

          skippedIndices.add(j);
          logger.info('Detected duplicate workout', {
            kept: bestWorkout.source,
            skipped: other === bestWorkout ? current.source : other.source,
            overlapPercent: Math.round(Math.max(overlapPercentCurrent, overlapPercentOther) * 100),
            date: current.startDate
          });
        }
      }

      result.push(bestWorkout);
    }

    logger.info('Deduplication complete', {
      original: workouts.length,
      deduplicated: result.length,
      removed: workouts.length - result.length
    });

    return result;
  }

  /**
   * Import workouts from Apple Health into Marune as exercises
   * Automatically deduplicates overlapping workouts from different sources (e.g., Whoop + Apple Watch)
   *
   * @param workouts - Array of HealthKit workouts to import
   * @param onProgress - Optional callback for progress updates
   * @returns Import result with counts and IDs
   */
  async importHealthWorkouts(
    workouts: HealthKitWorkout[],
    onProgress?: (current: number, total: number, item: string) => void,
    abortSignal?: AbortSignal
  ): Promise<ImportResult> {
    const result: ImportResult = {
      imported: 0,
      skipped: 0,
      failed: 0,
      importedIds: [],
      details: {
        deduplicated: 0,
        alreadyImported: 0,
        errors: []
      }
    };

    const user = getCurrentUser();
    if (!user) {
      logger.error('No user logged in for import');
      return result;
    }

    // Deduplicate overlapping workouts from different sources
    const deduplicatedWorkouts = this.deduplicateWorkouts(workouts);
    const duplicatesRemoved = workouts.length - deduplicatedWorkouts.length;

    logger.info('Starting health workout import', {
      original: workouts.length,
      afterDedup: deduplicatedWorkouts.length,
      duplicatesRemoved
    });

    // Add duplicates to skipped count
    result.skipped += duplicatesRemoved;
    result.details.deduplicated = duplicatesRemoved;

    // Bulk read: load all imported workout UUIDs upfront (one read instead of N)
    let importedUuids: Set<string>;
    try {
      const allImported = await getData(`users/${user.uid}/importedHealthWorkouts`);
      importedUuids = new Set(allImported ? Object.keys(allImported) : []);
      logger.info('Loaded imported workout index', { count: importedUuids.size });
    } catch (error) {
      logger.warn('Failed to load imported workout index, falling back to empty set', error);
      importedUuids = new Set();
    }

    // Filter out already-imported workouts locally (O(1) Set lookup)
    const toImport: HealthKitWorkout[] = [];
    for (const workout of deduplicatedWorkouts) {
      const sanitizedUuid = workout.uuid.replace(/[.#$/[\]]/g, '_');
      if (importedUuids.has(sanitizedUuid)) {
        result.skipped++;
        result.details.alreadyImported++;
      } else {
        toImport.push(workout);
      }
    }

    logger.info('Filtered already-imported workouts', {
      total: deduplicatedWorkouts.length,
      toImport: toImport.length,
      alreadyImported: deduplicatedWorkouts.length - toImport.length
    });

    // Import in batches of 20 with parallel writes within each batch
    const BATCH_SIZE = 20;

    for (let batchStart = 0; batchStart < toImport.length; batchStart += BATCH_SIZE) {
      // Check for abort
      if (abortSignal?.aborted) {
        logger.info('Import aborted by user', { imported: result.imported, remaining: toImport.length - batchStart });
        break;
      }

      const batch = toImport.slice(batchStart, batchStart + BATCH_SIZE);

      const batchPromises = batch.map(async (workout, batchIndex) => {
        const globalIndex = batchStart + batchIndex;
        onProgress?.(globalIndex + 1, toImport.length, workout.workoutActivityName);

        try {
          const sanitizedUuid = workout.uuid.replace(/[.#$/[\]]/g, '_');

          // Map HealthKit activity type to Marune exercise type
          const exerciseType = this.mapHealthKitToExerciseType(workout.workoutActivityId, workout.workoutActivityName);

          // Parse dates
          const startDate = new Date(workout.startDate);
          const durationMinutes = Math.round(workout.duration / 60);

          // Create exercise data
          const exerciseData = {
            date: startDate.getTime(),
            dateCreated: Date.now(),
            title: `${workout.workoutActivityName} (${workout.source})`,
            type: exerciseType,
            duration: durationMinutes,
            note: `Imported from ${workout.source}`,
            importedFrom: {
              provider: 'ios-healthKit' as const,
              sourceApp: workout.source,
              sourceBundleId: workout.sourceBundleId,
              originalUuid: workout.uuid,
              importedAt: Date.now(),
              originalData: {
                calories: workout.totalEnergyBurned,
                distance: workout.totalDistance
              }
            }
          };

          // Save exercise to Firebase
          const exerciseKey = await addExercise(user.uid, exerciseData);

          // Create import index entry for duplicate prevention
          const indexEntry: ImportedWorkoutIndex = {
            entityType: 'exercise',
            entityId: exerciseKey,
            importedAt: Date.now(),
            sourceApp: workout.source
          };
          await setData(`users/${user.uid}/importedHealthWorkouts/${sanitizedUuid}`, indexEntry);

          return { success: true as const, exerciseKey, uuid: workout.uuid, type: exerciseType, source: workout.source };
        } catch (error: any) {
          logger.error('Error importing workout', { uuid: workout.uuid, error });
          const errorMsg = error?.message || 'Unknown error';
          return { success: false as const, uuid: workout.uuid, errorMsg };
        }
      });

      const batchResults = await Promise.allSettled(batchPromises);

      for (const settled of batchResults) {
        if (settled.status === 'fulfilled') {
          const res = settled.value;
          if (res.success) {
            result.imported++;
            result.importedIds.push(res.exerciseKey);
            logger.info('Imported workout as exercise', {
              uuid: res.uuid,
              exerciseKey: res.exerciseKey,
              type: res.type,
              source: res.source
            });
          } else {
            result.failed++;
            if (res.errorMsg && result.details.errors.length < 5) {
              result.details.errors.push(res.errorMsg);
            }
          }
        } else {
          result.failed++;
          const reason = settled.reason?.message || String(settled.reason) || 'Promise rejected';
          if (result.details.errors.length < 5) {
            result.details.errors.push(reason);
          }
          logger.error('Batch import promise rejected', settled.reason);
        }
      }

      // Yield to UI thread between batches for smooth progress updates
      if (batchStart + BATCH_SIZE < toImport.length) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    // Invalidate query cache after successful import so next query is fresh
    if (result.imported > 0) {
      this.queryCache = null;
    }

    logger.info('Health workout import completed', result);
    return result;
  }

  /**
   * Full bidirectional resync: export Marune → Apple Health, then import Apple Health → Marune
   *
   * @param days - Number of days to sync (0 = all time)
   * @param onProgress - Optional callback for progress updates with phase indicator
   * @param abortSignal - Optional abort signal to cancel the operation
   * @returns Combined results from both export and import phases
   */
  async fullResync(
    days: number,
    onProgress?: (phase: ResyncPhase, current: number, total: number, item: string) => void,
    abortSignal?: AbortSignal
  ): Promise<ResyncResult> {
    const emptyImportResult: ImportResult = {
      imported: 0, skipped: 0, failed: 0, importedIds: [],
      details: { deduplicated: 0, alreadyImported: 0, errors: [] }
    };

    // Phase 1: Export (Marune → Apple Health)
    logger.info('Full resync: starting export phase', { days });
    const exportResult = await this.syncAllUnsynced(
      days,
      (current, total, item) => onProgress?.('export', current, total, item)
    );

    if (abortSignal?.aborted) {
      logger.info('Full resync: aborted after export phase');
      return { export: exportResult, import: emptyImportResult };
    }

    // Phase 2: Import (Apple Health → Marune)
    logger.info('Full resync: starting import phase', { days });
    const queryResult = await this.queryHealthWorkouts(days);
    let importResult: ImportResult = emptyImportResult;

    if (queryResult.workouts.length > 0) {
      importResult = await this.importHealthWorkouts(
        queryResult.workouts,
        (current, total, item) => onProgress?.('import', current, total, item),
        abortSignal
      );
    }

    logger.info('Full resync complete', {
      exported: exportResult.totalSynced,
      exportFailed: exportResult.totalFailed,
      imported: importResult.imported,
      importSkipped: importResult.skipped,
      importFailed: importResult.failed
    });

    return { export: exportResult, import: importResult };
  }

  /**
   * Map HealthKit activity type ID to Marune exercise type
   */
  private mapHealthKitToExerciseType(activityId: number, activityName: string): ExerciseType {
    // HealthKit activity type IDs: https://developer.apple.com/documentation/healthkit/hkworkoutactivitytype
    const map: Record<number, ExerciseType> = {
      // Running variants
      37: 'running',  // running

      // Cycling variants
      13: 'cycling',  // cycling

      // Swimming
      46: 'swimming',

      // HIIT
      63: 'hiit',

      // Yoga
      50: 'yoga',

      // Strength/Resistance training
      20: 'resistance', // functionalStrengthTraining
      35: 'resistance', // traditionalStrengthTraining
      52: 'resistance', // coreTraining

      // Other workout types that map to 'other'
      // Boxing, kickboxing, martial arts, etc. would go here
      // but since those are more like "trainings" in Marune, we'll use 'other'
    };

    // Check for running in the name
    if (activityName.toLowerCase().includes('run')) {
      return 'running';
    }
    if (activityName.toLowerCase().includes('cycling') || activityName.toLowerCase().includes('bike')) {
      return 'cycling';
    }
    if (activityName.toLowerCase().includes('swim')) {
      return 'swimming';
    }
    if (activityName.toLowerCase().includes('yoga')) {
      return 'yoga';
    }
    if (activityName.toLowerCase().includes('hiit') || activityName.toLowerCase().includes('interval')) {
      return 'hiit';
    }
    if (activityName.toLowerCase().includes('strength') || activityName.toLowerCase().includes('weight')) {
      return 'resistance';
    }

    return map[activityId] || 'other';
  }
}

// Export singleton instance
export const healthSyncService = new HealthSyncService();
export default healthSyncService;